// RegionSelectFields.jsx
import { useState, useEffect } from "react";
import { Field, ErrorMessage, useFormikContext } from "formik";

// Estilos comunes que pueden ser importados o pasados como props
const defaultStyles = {
  selectClass: "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 outline-none appearance-none bg-white shadow-sm",
  selectDisabledClass: "bg-[#aa2121] text-gray-500 cursor-not-allowed", // Clase para campos deshabilitados
  labelClass: "block mb-2 font-medium text-gray-700",
  errorClass: "text-sm text-red-500 mt-1 font-medium",
  helperTextClass: "text-xs text-gray-500 mt-1"
};

// Componente principal para regiones, provincias y comunas
const RegionProvinceDistrictFields = ({ 
  regions, 
  styles = defaultStyles,
  helperTexts = {},
  showLabels = true,
  disabled = false, // Añadir el prop disabled
}) => {
  const { values, setFieldValue } = useFormikContext();
  const [availableProvinces, setAvailableProvinces] = useState([]);
  const [availableDistricts, setAvailableDistricts] = useState([]);

  // Efecto para actualizar las provincias cuando cambia la región
  useEffect(() => {
    if (values.region) {
      const region = regions.find(r => r.name === values.region);
      if (region) {
        setAvailableProvinces(region.provinces || []);
      } else {
        setAvailableProvinces([]);
      }
    } else {
      setAvailableProvinces([]);
    }
  }, [values.region, regions]);

  // Efecto para actualizar las comunas cuando cambia la provincia
  useEffect(() => {
    if (values.province && availableProvinces.length > 0) {
      const province = availableProvinces.find(p => p.name === values.province);
      if (province) {
        setAvailableDistricts(province.districts || []);
      } else {
        setAvailableDistricts([]);
      }
    } else {
      setAvailableDistricts([]);
    }
  }, [values.province, availableProvinces]);

  // Efecto para inicializar los selects al cargar el componente
  useEffect(() => {
    if (values.region) {
      const region = regions.find(r => r.name === values.region);
      if (region) {
        setAvailableProvinces(region.provinces || []);
        
        if (values.province) {
          const province = region.provinces.find(p => p.name === values.province);
          if (province) {
            setAvailableDistricts(province.districts || []);
          }
        }
      }
    }
  }, []);

  // Manejador de cambio para la región
  const handleRegionChange = (e) => {
    const regionName = e.target.value;
    setFieldValue('region', regionName);
    setFieldValue('province', '');
    setFieldValue('district', '');
  };

  // Manejador de cambio para la provincia
  const handleProvinceChange = (e) => {
    const provinceName = e.target.value;
    setFieldValue('province', provinceName);
    setFieldValue('district', '');
  };

  return (
    <>
      <div className="mb-6">
        {showLabels && (
          <label htmlFor="region" className={styles.labelClass}>
            Región <span className="text-red-500">*</span>
          </label>
        )}
        <Field
          as="select"
          id="region"
          name="region"
          className={`${styles.selectClass} ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ""}`} // Aplicar clase deshabilitada si es necesario
          onChange={handleRegionChange}
          disabled={disabled} // Deshabilitar cuando 'disabled' es true
        >
          <option value="">Seleccione una región</option>
          {regions.map((region) => (
            <option key={region.name} value={region.name}>
              {region.name} 
            </option>
          ))}
        </Field>
        {!disabled && helperTexts.region && <p className={styles.helperTextClass}>{helperTexts.region}</p>}
        <ErrorMessage name="region" component="div" className={styles.errorClass} />
      </div>

      <div className="mb-6">
        {showLabels && (
          <label htmlFor="province" className={styles.labelClass}>
            Provincia <span className="text-red-500">*</span>
          </label>
        )}
        <Field
          as="select"
          id="province"
          name="province"
          className={`${styles.selectClass} ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ""}`} // Aplicar clase deshabilitada si es necesario
          onChange={handleProvinceChange}
          disabled={disabled || !values.region} // Deshabilitar si no hay región seleccionada o si 'disabled' es true
        >
          <option value="">Seleccione una provincia</option>
          {availableProvinces.map((province) => (
            <option key={province.name} value={province.name}>
              {province.name}
            </option>
          ))}
        </Field>
        {!disabled && helperTexts.province && (
          <p className={styles.helperTextClass}>{helperTexts.province}</p>
        )}

        <ErrorMessage name="province" component="div" className={styles.errorClass} />
      </div>

      <div className="mb-6">
        {showLabels && (
          <label htmlFor="district" className={styles.labelClass}>
            Comuna <span className="text-red-500">*</span>
          </label>
        )}
        <Field
          as="select"
          id="district"
          name="district"
          className={`${styles.selectClass} ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ""}`} // Aplicar clase deshabilitada si es necesario
          disabled={disabled || !values.province} // Deshabilitar si no hay provincia seleccionada o si 'disabled' es true
        >
          <option value="">Seleccione una comuna</option>
          {availableDistricts.map((district) => (
            <option key={district} value={district}>
              {district}
            </option>
          ))}
        </Field>
        {!disabled && helperTexts.district && <p className={styles.helperTextClass}>{helperTexts.district}</p>}
        <ErrorMessage name="district" component="div" className={styles.errorClass} />
      </div>
    </>
  );
};

export default RegionProvinceDistrictFields;
